// @djsem/shared — Release Campaign Planner (36), Post-Show Recorder (37),
// Library Cleanup (38), Pro SDK (39), Marketplace (35).
//
// Five modules in one file, each clearly delimited. All follow established
// contract + service patterns. No UI or backend routes — those are dependency-reported.

import { type DJSEMValidationResult, toDJSEMValidationResult, type ValidationResult } from "../index";
import { SeqCounter } from "../runtime";
const AT = "1970-01-01T00:00:00.000Z";
const _seq = new SeqCounter();

// ============================================================================
// PROMPT 36 — Release Campaign Planner
// ============================================================================

export type DJSEMCampaignPlatform =
  | "instagram" | "tiktok" | "youtube" | "soundcloud" | "spotify"
  | "bandcamp" | "facebook" | "x" | "website" | "email" | "press";

export type DJSEMCampaignPhaseLabel =
  | "announcement" | "teaser" | "pre_save" | "release_day" | "post_release" | "gig_promo" | "custom";

export type DJSEMCampaignTaskStatus = "todo" | "in_progress" | "ready" | "posted_manually" | "skipped";

export interface DJSEMCampaignTask {
  id: string;
  platform: DJSEMCampaignPlatform;
  title: string;
  copyDraft?: string;
  assetIds: string[];
  dueDate?: string;
  status: DJSEMCampaignTaskStatus;
  warnings: string[];
}

export interface DJSEMCampaignPhase {
  id: string;
  label: DJSEMCampaignPhaseLabel;
  startDate?: string;
  endDate?: string;
  tasks: DJSEMCampaignTask[];
}

export interface DJSEMReleaseCampaign {
  id: string;
  projectId: string;
  releaseId: string;
  title: string;
  releaseDate?: string;
  status: "draft" | "planned" | "active" | "completed" | "archived";
  phases: DJSEMCampaignPhase[];
  requiredAssetIds: string[];
  warnings: string[];
  createdAt: string;
  updatedAt: string;
}

export interface CreateReleaseCampaignInput { projectId: string; releaseId: string; title: string; releaseDate?: string; }
export interface AddCampaignTaskInput { campaign: DJSEMReleaseCampaign; phaseIndex: number; task: Omit<DJSEMCampaignTask, "id">; }
export interface UpdateCampaignTaskInput { campaign: DJSEMReleaseCampaign; taskId: string; status: DJSEMCampaignTaskStatus; }
export interface LinkAssetToTaskInput { campaign: DJSEMReleaseCampaign; taskId: string; assetId: string; }

// Default single-release campaign plan
function defaultSingleReleasePlan(campaignId: string): DJSEMCampaignPhase[] {
  return [
    { id: `ph_${_seq.next()}`, label: "teaser", tasks: [
      { id: `ct_${_seq.next()}`, platform: "instagram", title: "Teaser post", assetIds: [], status: "todo", warnings: [] },
      { id: `ct_${_seq.next()}`, platform: "tiktok", title: "Short teaser clip", assetIds: [], status: "todo", warnings: [] },
    ]},
    { id: `ph_${_seq.next()}`, label: "release_day", tasks: [
      { id: `ct_${_seq.next()}`, platform: "instagram", title: "Release artwork post", assetIds: [], status: "todo", warnings: [] },
      { id: `ct_${_seq.next()}`, platform: "youtube", title: "Visualizer premiere", assetIds: [], status: "todo", warnings: [] },
      { id: `ct_${_seq.next()}`, platform: "spotify", title: "Canvas loop active", assetIds: [], status: "todo", warnings: [] },
    ]},
    { id: `ph_${_seq.next()}`, label: "post_release", tasks: [
      { id: `ct_${_seq.next()}`, platform: "instagram", title: "Behind the scenes", assetIds: [], status: "todo", warnings: [] },
    ]},
  ];
}

export class ReleaseCampaignPlannerService {
  createCampaign(input: CreateReleaseCampaignInput): DJSEMReleaseCampaign {
    const id = `rc_${_seq.next()}`;
    return {
      id, projectId: input.projectId, releaseId: input.releaseId,
      title: input.title, releaseDate: input.releaseDate,
      status: "draft", phases: defaultSingleReleasePlan(id),
      requiredAssetIds: [], warnings: [],
      createdAt: AT, updatedAt: AT,
    };
  }

  addTask(input: AddCampaignTaskInput): DJSEMReleaseCampaign {
    const phases = input.campaign.phases.map((p, i) => {
      if (i !== input.phaseIndex) return p;
      return { ...p, tasks: [...p.tasks, { ...input.task, id: `ct_${_seq.next()}` }] };
    });
    return { ...input.campaign, phases, updatedAt: AT };
  }

  updateTask(input: UpdateCampaignTaskInput): DJSEMReleaseCampaign {
    const phases = input.campaign.phases.map(p => ({
      ...p, tasks: p.tasks.map(t => t.id === input.taskId ? { ...t, status: input.status } : t),
    }));
    return { ...input.campaign, phases, updatedAt: AT };
  }

  linkAssetToTask(input: LinkAssetToTaskInput): DJSEMReleaseCampaign {
    const phases = input.campaign.phases.map(p => ({
      ...p, tasks: p.tasks.map(t => t.id === input.taskId ? { ...t, assetIds: [...t.assetIds, input.assetId] } : t),
    }));
    return { ...input.campaign, phases, updatedAt: AT };
  }

  validateCampaign(campaign: DJSEMReleaseCampaign): DJSEMValidationResult {
    const issues: ValidationResult["issues"] = [];
    const missingAssets = campaign.phases.flatMap(p => p.tasks).filter(t => t.assetIds.length === 0 && t.status !== "skipped");
    if (missingAssets.length > 0) issues.push({ path: "$.tasks", message: `${missingAssets.length} task(s) missing assets` });
    return toDJSEMValidationResult({ valid: issues.length === 0, issues }, "campaign");
  }
}
